import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  InvoiceLogoTile,
  LogoDestinationTiles,
} from "@/components/features/company-image/LogoDestinationTiles";
import { FileUpload } from "@/components/shared/FileUpload";
import { JPG_PNG_ACCEPT } from "@/components/features/company-image/invoiceLogoPreview";
import { InvoiceBrandColorSection } from "@/components/features/company-image/InvoiceBrandColorSection";
import { InvoiceLogoCropDialog } from "@/components/features/company-image/InvoiceLogoCropDialog";
import type { InvoiceLogoCropPayload, TenantCurrentResponse } from "@/services/authService";

export interface CompanyInvoiceIdentitySectionProps {
  tenant: TenantCurrentResponse;
  /**
   * When given, the portal tile is rendered SIDE BY SIDE with the invoice one
   * under the "personalizá tu factura" heading — the combined layout the
   * onboarding surfaces have always shown. Omitted (Settings), only the
   * invoice tile renders, because the portal logo lives in its own card there.
   */
  portalLogoUrl?: string | null;
  /** Combined mode only: the SOURCE image picker. It lives here rather than in
   *  the card because picking a source while "same image for both" is on has to
   *  open the crop editor, and the crop state belongs to this section. In
   *  Settings the card renders a plain site-logo picker instead — there the
   *  invoice image is chosen in this section's own tile. */
  onSiteLogoChange?: (file: File | null) => void;
  siteUploading?: boolean;
  useSameLogo: boolean;
  onUseSameLogoChange: (value: boolean) => void;
  bothUploading: boolean;
  invoiceUploading: boolean;
  onInvoiceLogoChange: (file: File | null) => void;
  onSaveBrandColor: (brandColor: string | null) => Promise<void>;
  brandColorSaving: boolean;
  disabled?: boolean;
  onPreviewInvoice?: () => void;
  previewPdfLoading?: boolean;
  onSaveInvoiceLogoCrop?: (payload: InvoiceLogoCropPayload) => Promise<void>;
  invoiceLogoCropSaving?: boolean;
  onUploadInvoiceLogoWithCrop?: (
    file: File,
    target: "both" | "invoice",
    crop: InvoiceLogoCropPayload
  ) => Promise<void>;
}

/**
 * The legal entity's INVOICE identity: the logo as it lands on the PDF, its
 * crop and size, and the brand colour.
 *
 * Extracted from CompanyImageCard because this is the same thing a marca is —
 * the identity a client is billed under — while the rest of that card is the
 * portal's own logo. Settings renders this inside the Marcas list, as the row
 * for "no marca"; the onboarding surfaces keep it inline in CompanyImageCard,
 * where one combined card is the right amount of ceremony.
 */
export function CompanyInvoiceIdentitySection({
  tenant,
  portalLogoUrl,
  onSiteLogoChange,
  siteUploading,
  useSameLogo,
  onUseSameLogoChange,
  bothUploading,
  invoiceUploading,
  onInvoiceLogoChange,
  onSaveBrandColor,
  brandColorSaving,
  disabled,
  onPreviewInvoice,
  previewPdfLoading,
  onSaveInvoiceLogoCrop,
  invoiceLogoCropSaving,
  onUploadInvoiceLogoWithCrop,
}: CompanyInvoiceIdentitySectionProps): JSX.Element {
  const { t } = useTranslation();
  const [cropOpen, setCropOpen] = useState(false);

  // Same resolution the PDF renderer uses (invoice logo, else site logo), so
  // the editor always crops the image that will actually appear on the invoice
  // — including when "use the same image for both" is on.
  const effectiveInvoiceLogoUrl = tenant.invoiceLogoImage ?? tenant.profileImage;

  // ---- Crop BEFORE upload ----
  // Picking a file opens the editor against a local object URL; nothing is sent
  // until the user confirms. Uploading first and prompting afterwards committed
  // the change before the user had approved how it looks, and the modal read as
  // an ambush. Cancelling here abandons the whole change.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  const combined = portalLogoUrl !== undefined;
  /**
   * "both" keeps the site logo in sync when the user asked for one image —
   * only meaningful in the combined card, where one picker feeds both
   * destinations. In Settings this tile edits the INVOICE identity alone, so
   * an upload here must never reach the portal logo: that is a separate card
   * about a different thing.
   */
  const uploadTarget: "both" | "invoice" =
    combined && useSameLogo ? "both" : "invoice";

  const cropImageUrl = pendingUrl ?? effectiveInvoiceLogoUrl;

  const clearPending = (): void => {
    setPendingUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev); // object URLs leak until revoked
      return null;
    });
    setPendingFile(null);
  };

  useEffect(() => {
    return () => {
      if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    };
  }, [pendingUrl]);

  const startCropForFile = (file: File | null): void => {
    if (!file) return;
    setPendingFile(file);
    setPendingUrl(URL.createObjectURL(file));
    setCropOpen(true);
  };

  // Memoised so its reference is stable across parent re-renders. An inline
  // literal here re-seeded the dialog mid-save and wiped the user's drag.
  const cropInitial = useMemo<InvoiceLogoCropPayload>(
    () =>
      pendingFile
        ? // A pending file is a NEW image, so it starts uncropped — reusing the
          // stored crop would frame the wrong picture.
          {
            invoiceLogoCropX: 0,
            invoiceLogoCropY: 0,
            invoiceLogoCropW: 1,
            invoiceLogoCropH: 1,
            invoiceLogoSizePt: 76,
          }
        : {
            invoiceLogoCropX: tenant.invoiceLogoCropX,
            invoiceLogoCropY: tenant.invoiceLogoCropY,
            invoiceLogoCropW: tenant.invoiceLogoCropW,
            invoiceLogoCropH: tenant.invoiceLogoCropH,
            invoiceLogoSizePt: tenant.invoiceLogoSizePt,
          },
    [
      pendingFile,
      tenant.invoiceLogoCropX,
      tenant.invoiceLogoCropY,
      tenant.invoiceLogoCropW,
      tenant.invoiceLogoCropH,
      tenant.invoiceLogoSizePt,
    ]
  );

  const handleCropDialogOpenChange = (next: boolean): void => {
    setCropOpen(next);
    if (!next) clearPending(); // cancel === abandon the pending upload
  };

  const anyUploading = Boolean(disabled) || invoiceUploading || bothUploading;

  const invoiceTileProps = {
    invoiceLogoUrl: effectiveInvoiceLogoUrl,
    crop: {
      x: tenant.invoiceLogoCropX,
      y: tenant.invoiceLogoCropY,
      w: tenant.invoiceLogoCropW,
      h: tenant.invoiceLogoCropH,
      sizePt: tenant.invoiceLogoSizePt,
    },
    shared: useSameLogo,
    onSharedChange: onUseSameLogoChange,
    onAdjust: () => setCropOpen(true),
    // Must mirror the InvoiceLogoCropDialog render condition below. This used
    // to key on invoiceLogoUrl alone, so on any surface that didn't pass
    // onSaveInvoiceLogoCrop it set cropOpen and mounted nothing — a dead
    // button, which is what an accountant hit on the client-onboarding page.
    canAdjust: Boolean(onSaveInvoiceLogoCrop && cropImageUrl),
    onInvoiceLogoPicked: onUploadInvoiceLogoWithCrop
      ? startCropForFile
      : onInvoiceLogoChange,
    invoiceUploading: invoiceUploading || bothUploading,
    disabled: anyUploading,
    showSharedToggle: combined,
  };

  return (
    <div className="space-y-6">
      {combined && onSiteLogoChange && (
        <>
          <FileUpload
            key={`source-${tenant.profileImage ?? "default"}`}
            currentImageUrl={tenant.profileImage}
            onFileChange={
              useSameLogo && onUploadInvoiceLogoWithCrop
                ? startCropForFile
                : onSiteLogoChange
            }
            disabled={anyUploading}
            showLabel={false}
            acceptedMimeTypes={JPG_PNG_ACCEPT}
          />
          {(bothUploading || siteUploading) && !onUploadInvoiceLogoWithCrop && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t("settings.companyImage.uploading")}</span>
            </div>
          )}
        </>
      )}

      {combined ? (
        <LogoDestinationTiles
          portalLogoUrl={portalLogoUrl ?? null}
          {...invoiceTileProps}
        />
      ) : (
        <InvoiceLogoTile {...invoiceTileProps} />
      )}

      {onSaveInvoiceLogoCrop && cropImageUrl && (
        <InvoiceLogoCropDialog
          open={cropOpen}
          onOpenChange={handleCropDialogOpenChange}
          imageUrl={cropImageUrl}
          initial={cropInitial}
          isNewUpload={Boolean(pendingFile)}
          saving={Boolean(invoiceLogoCropSaving) || anyUploading}
          onSave={async (payload) => {
            if (pendingFile && onUploadInvoiceLogoWithCrop) {
              await onUploadInvoiceLogoWithCrop(pendingFile, uploadTarget, payload);
            } else {
              await onSaveInvoiceLogoCrop(payload);
            }
            setCropOpen(false);
            clearPending();
          }}
        />
      )}

      {/* Colour only in the COMBINED card. In Settings it is its own row
          action in the Marcas list, so every identity changes colour the same
          way instead of it hiding inside one row's logo editor. */}
      {combined && (
        <InvoiceBrandColorSection
          logoUrl={effectiveInvoiceLogoUrl}
          savedColor={tenant.brandColor}
          saving={brandColorSaving}
          disabled={anyUploading}
          onSave={onSaveBrandColor}
        />
      )}

      {/* See the real thing. Every setting above only pays off once you can
          look at the actual PDF, so this stays a primary action. */}
      {onPreviewInvoice && (
        <div className="rounded-lg border border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)] p-4 dark:border-[var(--dk-border)] dark:bg-[var(--dk-surface2)] sm:flex sm:items-center sm:justify-between sm:gap-4">
          <div className="mb-3 sm:mb-0">
            <div className="text-sm font-semibold">
              {t("settings.previewInvoice.promptTitle")}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("settings.previewInvoice.promptHelp")}
            </p>
          </div>
          <Button
            type="button"
            onClick={onPreviewInvoice}
            disabled={Boolean(previewPdfLoading)}
            className="w-full shrink-0 transition-colors duration-150 sm:w-auto"
          >
            {previewPdfLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("settings.previewInvoice.generating")}
              </>
            ) : (
              <>
                <Eye className="mr-2 h-4 w-4" />
                {t("settings.previewInvoice.button")}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
